/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Type } from '@gh/core-type';
import { cloneDeep, constant, defaults, findIndex, isFunction, isNil, mapValues } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { CellParams, EditableCellAccessor, GridModel } from './grid-state.interfaces';

export interface MockGridModelEditableField {
  editable?: boolean | PredicateFn<any>;
  required?: boolean | PredicateFn<any>;
  type?: Type<any>;
}

export interface MockGridModelOptions {
  idProperty: string;
  editableFields?: Hash<MockGridModelEditableField>;
  onDirty?: { field: string; value: any };
}

function normalizeFieldSettings(field: MockGridModelEditableField): MockGridModelEditableField {
  return defaults({}, field || {}, { editable: true, required: false });
}

function mockCellPredicate(predicate: Maybe<boolean | PredicateFn<any>>): PredicateFn<CellParams> {
  if (isFunction(predicate)) {
    return ({ data }) => predicate(data);
  } else if (predicate) {
    return constant(predicate);
  } else {
    return constant(false);
  }
}

export class MockGridModel implements GridModel {
  rowData = new Subject<any[]>();
  editable = new BehaviorSubject(false);
  private rowDataSnapshot: any[];
  private options: MockGridModelOptions;

  constructor(options: MockGridModelOptions) {
    this.options = {
      ...options,
      editableFields: mapValues(options.editableFields || {}, normalizeFieldSettings),
    };
  }

  setData(data: any): void {
    this.rowData.next(data);
    this.rowDataSnapshot = data;
  }

  getEntityId(entity: any): any {
    return entity[this.options.idProperty];
  }

  getCellAccessor(name: string): EditableCellAccessor {
    const { required, type, editable } = this.getFieldSettings(name);

    const validate = (params: CellParams, value: any) => {
      if (required && (isNil(value) || value === '')) {
        return { required: false };
      }
      if (type) {
        return type.validate(value);
      }

      return void 0;
    };

    return {
      name,
      isEditable: mockCellPredicate(editable),
      isRequired: mockCellPredicate(required),
      getState: (params) => {
        const { data, colDef: { field } } = params;
        const value = field && data[field];
        return { value, errors: validate(params, value) };
      },
      getValue: (state) => state.value,
      setValue: (params, _, value) => {
        const { onDirty } = this.options;
        const { data, colDef: { field } } = params;

        const newData = cloneDeep(data);

        if (field) {
          newData[field] = value;
        }

        if (onDirty) {
          newData[onDirty.field] = onDirty.value;
        }

        const { rowDataSnapshot } = this;
        const index = findIndex(rowDataSnapshot, data);
        this.setData(rowDataSnapshot.slice(0, index)
          .concat([newData])
          .concat(rowDataSnapshot.slice(index + 1)));
      },
      validate,
    };
  }

  private getFieldSettings(name: string): MockGridModelEditableField {
    return normalizeFieldSettings(this.options.editableFields![name]); // tslint:disable-line:no-non-null-assertion
  }
}
